import React from 'react';
import {
  Paper,
  Typography,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Button,
  IconButton,
  Collapse,
} from '@mui/material';
import {
  FilterList,
  Clear,
  ExpandMore,
  ExpandLess,
} from '@mui/icons-material';

interface Filters {
  search: string;
  eventType: string;
  status: string;
  priority: string;
  startDateFrom: string;
  startDateTo: string;
  endDateFrom: string;
  endDateTo: string;
  allDay: string;
}

interface CalendarFiltersProps {
  filters: Filters;
  onFilterChange: (filters: Partial<Filters>) => void;
}

const CalendarFilters: React.FC<CalendarFiltersProps> = ({
  filters,
  onFilterChange,
}) => {
  const [expanded, setExpanded] = React.useState(false);

  const handleInputChange = (field: keyof Filters, value: string) => {
    onFilterChange({ [field]: value });
  };

  const handleClearFilters = () => {
    onFilterChange({
      search: '',
      eventType: '',
      status: '',
      priority: '',
      startDateFrom: '',
      startDateTo: '',
      endDateFrom: '',
      endDateTo: '',
      allDay: '',
    });
  };

  const eventTypeOptions = [
    { value: 'client_meeting', label: 'Client Meeting' },
    { value: 'court_hearing', label: 'Court Hearing' },
    { value: 'deposition', label: 'Deposition' },
    { value: 'mediation', label: 'Mediation' },
    { value: 'consultation', label: 'Consultation' },
    { value: 'filing_deadline', label: 'Filing Deadline' },
    { value: 'document_review', label: 'Document Review' },
    { value: 'billing_review', label: 'Billing Review' },
    { value: 'staff_meeting', label: 'Staff Meeting' },
    { value: 'training', label: 'Training' },
    { value: 'other', label: 'Other' },
  ];

  const statusOptions = [
    { value: 'scheduled', label: 'Scheduled' },
    { value: 'confirmed', label: 'Confirmed' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'completed', label: 'Completed' },
    { value: 'no_show', label: 'No Show' },
  ];

  const priorityOptions = [
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
    { value: 'urgent', label: 'Urgent' },
  ];

  const allDayOptions = [
    { value: '', label: 'All Events' },
    { value: 'true', label: 'All Day Events Only' },
    { value: 'false', label: 'Timed Events Only' },
  ];

  const hasActiveFilters = Object.values(filters).some(value => value !== '');

  return (
    <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FilterList />
          <Typography variant="h6">Filter Events</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {hasActiveFilters && (
            <Button
              size="small"
              onClick={handleClearFilters}
              startIcon={<Clear />}
            >
              Clear Filters
            </Button>
          )}
          <IconButton
            size="small"
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>
      </Box>

      <Collapse in={expanded}>
        <Grid container spacing={2}>
          {/* Search */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Search Events"
              placeholder="Search by title, description..."
              value={filters.search}
              onChange={(e) => handleInputChange('search', e.target.value)}
              size="small"
            />
          </Grid>

          {/* Event Type */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Event Type</InputLabel>
              <Select
                value={filters.eventType}
                onChange={(e) => handleInputChange('eventType', e.target.value)}
                label="Event Type"
              >
                <MenuItem value="">All Types</MenuItem>
                {eventTypeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Status */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                onChange={(e) => handleInputChange('status', e.target.value)}
                label="Status"
              >
                <MenuItem value="">All Statuses</MenuItem>
                {statusOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Priority */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Priority</InputLabel>
              <Select
                value={filters.priority}
                onChange={(e) => handleInputChange('priority', e.target.value)}
                label="Priority"
              >
                <MenuItem value="">All Priorities</MenuItem>
                {priorityOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* All Day Filter */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Event Duration</InputLabel>
              <Select
                value={filters.allDay}
                onChange={(e) => handleInputChange('allDay', e.target.value)}
                label="Event Duration"
              >
                {allDayOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Start Date From */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Start Date From"
              type="date"
              value={filters.startDateFrom}
              onChange={(e) => handleInputChange('startDateFrom', e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          {/* Start Date To */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Start Date To"
              type="date"
              value={filters.startDateTo}
              onChange={(e) => handleInputChange('startDateTo', e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          {/* End Date From */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="End Date From"
              type="date"
              value={filters.endDateFrom}
              onChange={(e) => handleInputChange('endDateFrom', e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          {/* End Date To */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="End Date To"
              type="date"
              value={filters.endDateTo}
              onChange={(e) => handleInputChange('endDateTo', e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>
      </Collapse>
    </Paper>
  );
};

export default CalendarFilters; 